/**
 * Preview Component
 *
 * Renders invitation templates with device selection.
 * Optimizations: memo, useDeferredValue, dynamic imports, memoized styles
 */

'use client'

import {
  useState,
  useMemo,
  useCallback,
  createElement,
  useEffect,
  useRef,
  memo,
  useDeferredValue,
  Component,
} from 'react'
import type { TemplateData, TemplateComponent } from '@kiltechco/dday-templates'
import { Expand, RefreshCw } from 'lucide-react'
import SingleSelectDropdown from '@/components/ui/Dropdown/SingleSelectDropdown'
import { Tooltip } from '@/components/ui/Tooltip'
import { getPackageConfig } from '@/lib/ddayTemplates'
import { logger } from '@/lib/logger'
import { ensureRuntimeConfigWithMapsKey } from '@/src/lib/runtime-config-bootstrap.client'

// =============================================================================
// Error Boundary for Template Rendering
// =============================================================================

interface ErrorBoundaryProps {
  children: React.ReactNode
  onReset?: () => void
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class TemplateErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError?.(error, errorInfo)

    // Log to Sentry but don't spam for known Google Maps race conditions
    const isGoogleMapsError =
      error.message?.includes('Bo is not a function') ||
      error.stack?.includes('maps-api-v3')

    if (isGoogleMapsError) {
      logger.warn(
        'Google Maps render race condition (non-fatal):',
        error.message
      )
    } else {
      logger.error('Template render error:', error, errorInfo)
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
    this.props.onReset?.()
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-muted h-full flex items-center justify-center">
          <div className="text-center p-8">
            <p className="text-sm text-muted-foreground mb-3">
              Preview temporarily unavailable
            </p>
            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-primary hover:text-primary/80 bg-primary/10 rounded-md transition-colors cursor-pointer"
            >
              <RefreshCw className="w-3 h-3" />
              Retry
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// =============================================================================
// Types
// =============================================================================

interface PreviewProps {
  templateData: TemplateData
  refreshKey?: number
  onFullPagePreview?: () => void
}

interface DevicePreviewProps {
  width: number
  height: number
  scale: number
  backgroundColor: string
  style?: React.CSSProperties
  children: React.ReactNode
}

type DevicePreviewComponent = React.ComponentType<DevicePreviewProps>

interface TemplateRendererProps {
  templateData: TemplateData
  viewportWidth: number
  viewportHeight: number
  scale: number
  height: string
}

interface PhoneFrameProps {
  device: DeviceConfig
  scale: number
  children: React.ReactNode
}

interface DeviceConfig {
  name: string
  width: number
  height: number
  noFrame?: boolean
}

type DeviceKey = keyof typeof DEVICES

// =============================================================================
// Constants
// =============================================================================

const DEVICES = {
  default: { name: 'Default', width: 500, height: 1050, noFrame: true },
  'iphone-11-xr': { name: 'iPhone 11/XR (414×896)', width: 414, height: 896 },
  'iphone-12-14': {
    name: 'iPhone 12/13/14 (390×844)',
    width: 390,
    height: 844,
  },
  'iphone-13-mini': {
    name: 'iPhone 13 mini (375×812)',
    width: 375,
    height: 812,
  },
  'iphone-14-pro': { name: 'iPhone 14 Pro (393×852)', width: 393, height: 852 },
  'iphone-15': { name: 'iPhone 15 (393×852)', width: 393, height: 852 },
  'iphone-15-pro-max': {
    name: 'iPhone 15 Pro Max (430×932)',
    width: 430,
    height: 932,
  },
  'samsung-s24': { name: 'Samsung S24 (412×915)', width: 412, height: 915 },
  'galaxy-a': { name: 'Galaxy A series (360×800)', width: 360, height: 800 },
} as const

const DEVICE_OPTIONS = (Object.keys(DEVICES) as DeviceKey[]).map((key) => ({
  value: key,
  label: DEVICES[key].name,
}))

const PREVIEW_SCALE = 0.6
const BEZEL_SIZE = 12
const SHELL_PADDING = 24
const CORNER_RADIUS = { outer: 40, inner: 32 }
const HOME_INDICATOR = { width: 134, height: 5 }

const SIDE_BUTTONS = {
  left: [
    { top: 120, height: 24 },
    { top: 170, height: 50 },
    { top: 230, height: 50 },
  ],
  right: [{ top: 180, height: 70 }],
} as const

const SCROLLBAR_HIDE =
  'scrollbar-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'

const SHELL_GRADIENT =
  'bg-linear-to-b from-zinc-800 via-zinc-900 to-black dark:from-zinc-100 dark:via-zinc-200 dark:to-zinc-300'

const SHELL_SHADOW = {
  boxShadow:
    '0 20px 40px -10px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1) inset',
} as const

// =============================================================================
// PhoneFrame Component (Memoized)
// =============================================================================

const PhoneFrame = memo(function PhoneFrame({
  device,
  scale,
  children,
}: PhoneFrameProps) {
  const shellStyle = useMemo(
    () => ({
      width: device.width + SHELL_PADDING,
      height: device.height + SHELL_PADDING,
      transform: `scale(${scale})`,
      transformOrigin: 'top left' as const,
    }),
    [device.width, device.height, scale]
  )

  const screenStyle = useMemo(
    () => ({
      top: BEZEL_SIZE,
      left: BEZEL_SIZE,
      width: device.width,
      height: device.height,
    }),
    [device.width, device.height]
  )

  return (
    <div
      className={`absolute inset-0 rounded-[${CORNER_RADIUS.outer}px] ${SHELL_GRADIENT} overflow-visible`}
      style={shellStyle}
    >
      <div
        className={`absolute inset-0 rounded-[${CORNER_RADIUS.outer}px] pointer-events-none`}
        style={SHELL_SHADOW}
      />

      {SIDE_BUTTONS.left.map((btn, i) => (
        <div
          key={`l${i}`}
          className="absolute bg-zinc-600 dark:bg-zinc-400 rounded-l-sm"
          style={{ left: -3, top: btn.top, width: 3, height: btn.height }}
        />
      ))}

      {SIDE_BUTTONS.right.map((btn, i) => (
        <div
          key={`r${i}`}
          className="absolute bg-zinc-600 dark:bg-zinc-400 rounded-r-sm"
          style={{ right: -3, top: btn.top, width: 3, height: btn.height }}
        />
      ))}

      <div
        className={`absolute bg-black rounded-[${CORNER_RADIUS.inner}px] overflow-hidden ${SCROLLBAR_HIDE}`}
        style={screenStyle}
      >
        <div className={`w-full h-full ${SCROLLBAR_HIDE}`}>{children}</div>
        <div
          className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-white/80 dark:bg-black/80 rounded-full z-20"
          style={{ width: HOME_INDICATOR.width, height: HOME_INDICATOR.height }}
        />
      </div>
    </div>
  )
})

// =============================================================================
// TemplateRenderer Component (Memoized)
// =============================================================================

const TemplateRenderer = memo(function TemplateRenderer({
  templateData,
  viewportWidth,
  viewportHeight,
  scale,
  height,
}: TemplateRendererProps) {
  // Only trigger fallback for the known lazy intro module failures.
  const isLazyIntroResolutionError = useCallback((error: Error) => {
    const message = error.message || ''
    return (
      message.includes('Minified React error #306') ||
      message.includes('Element type is invalid')
    )
  }, [])

  const [templateComponent, setTemplateComponent] =
    useState<TemplateComponent | null>(null)
  const [DevicePreview, setDevicePreview] =
    useState<DevicePreviewComponent | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // Start in embedded mode; downgrade to standalone once if intro lazy render fails.
  const [renderContext, setRenderContext] = useState<'embedded' | 'standalone'>(
    'embedded'
  )
  const [didFallbackToStandalone, setDidFallbackToStandalone] = useState(false)
  const isMountedRef = useRef(true)

  const devicePreviewStyle = useMemo(
    () => ({
      borderRadius: CORNER_RADIUS.inner * scale,
      overflow: 'hidden' as const,
    }),
    [scale]
  )

  useEffect(() => {
    isMountedRef.current = true

    const loadTemplate = async () => {
      try {
        const pkg = await import('@kiltechco/dday-templates')
        if (!isMountedRef.current) return

        await ensureRuntimeConfigWithMapsKey()
        if (!isMountedRef.current) return

        // Set package config (environment, CDN, Maps API)
        pkg.setPackageConfig(getPackageConfig())

        const Template = await pkg.getTemplate()
        if (!isMountedRef.current) return

        setDevicePreview(() => pkg.DevicePreview)
        setTemplateComponent(() => Template)
        setStatus('ready')
      } catch (err) {
        if (!isMountedRef.current) return
        logger.error('Template loading error:', err)
        setErrorMessage(err instanceof Error ? err.message : 'Unknown error')
        setStatus('error')
      }
    }

    loadTemplate()
    return () => {
      isMountedRef.current = false
      setTemplateComponent(null)
      setDevicePreview(null)
    }
  }, [])

  const templateElement = useMemo(() => {
    if (!templateComponent) return null
    try {
      return createElement(templateComponent, {
        templateData,
        mode: 'preview' as const,
        renderContext,
        disableGlobalScrollLock: true,
        viewportWidth,
        height,
      })
    } catch {
      return null
    }
  }, [templateComponent, templateData, viewportWidth, height, renderContext])

  const handleTemplateError = useCallback(
    (error: Error) => {
      if (
        renderContext === 'embedded' &&
        !didFallbackToStandalone &&
        isLazyIntroResolutionError(error)
      ) {
        logger.warn(
          'Embedded preview hit lazy intro render error. Falling back to standalone renderContext.',
          error.message
        )
        setDidFallbackToStandalone(true)
        setRenderContext('standalone')
      }
    },
    [didFallbackToStandalone, isLazyIntroResolutionError, renderContext]
  )

  if (status === 'loading') {
    return (
      <div className="bg-muted h-full flex items-center justify-center">
        <div className="text-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading template...</p>
        </div>
      </div>
    )
  }

  if (status === 'error' || !templateElement || !DevicePreview) {
    return (
      <div className="bg-muted h-full flex items-center justify-center">
        <p className="text-sm text-muted-foreground/70 p-8">
          {errorMessage || 'Template not found'}
        </p>
      </div>
    )
  }

  return (
    <TemplateErrorBoundary key={renderContext} onError={handleTemplateError}>
      <DevicePreview
        width={viewportWidth}
        height={viewportHeight}
        scale={scale}
        backgroundColor="#ffffff"
        style={devicePreviewStyle}
      >
        {templateElement}
      </DevicePreview>
    </TemplateErrorBoundary>
  )
})

// =============================================================================
// Main Preview Component
// =============================================================================

export default function Preview({
  templateData,
  refreshKey,
  onFullPagePreview,
}: PreviewProps) {
  // Mobile (≤768px) defaults to iPhone 15 so the preview reflects a real phone layout.
  // Lazy initializer avoids a cascading re-render; typeof window guard covers SSR.
  const [selectedDevice, setSelectedDevice] = useState<DeviceKey>(() =>
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 768px)').matches
      ? 'iphone-15'
      : 'default'
  )
  const deferredTemplateData = useDeferredValue(templateData)

  const device = DEVICES[selectedDevice] as DeviceConfig

  const handleDeviceChange = useCallback((value: string) => {
    setSelectedDevice(value as DeviceKey)
  }, [])

  const containerStyle = useMemo(
    () =>
      device.noFrame
        ? {
            width: device.width * PREVIEW_SCALE,
            height: device.height * PREVIEW_SCALE,
          }
        : {
            width: (device.width + SHELL_PADDING) * PREVIEW_SCALE,
            height: (device.height + SHELL_PADDING) * PREVIEW_SCALE,
          },
    [device.width, device.height, device.noFrame]
  )

  const defaultViewStyle = useMemo(
    () => ({
      width: device.width,
      height: device.height,
      transform: `scale(${PREVIEW_SCALE})`,
      transformOrigin: 'top left' as const,
    }),
    [device.width, device.height]
  )

  return (
    <div className="flex flex-col items-center">
      <div className="mb-4 flex items-center gap-2">
        <Tooltip
          content="Choose a device to preview how the invitation looks"
          side="top"
        >
          <div>
            <SingleSelectDropdown
              value={selectedDevice}
              onChange={handleDeviceChange}
              options={DEVICE_OPTIONS}
              placeholder="Select device"
            />
          </div>
        </Tooltip>
        {onFullPagePreview && (
          <Tooltip content="Open full screen preview" side="bottom">
            <button
              onClick={onFullPagePreview}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground bg-card border border-border rounded-lg hover:bg-muted transition-colors cursor-pointer"
            >
              <Expand className="w-4 h-4" />
              Full
            </button>
          </Tooltip>
        )}
      </div>

      <div className="relative overflow-visible" style={containerStyle}>
        {device.noFrame ? (
          <div className="absolute inset-0" style={defaultViewStyle}>
            <TemplateRenderer
              key={refreshKey}
              templateData={deferredTemplateData}
              viewportWidth={device.width}
              viewportHeight={device.height}
              scale={1}
              height={`${device.height}px`}
            />
          </div>
        ) : (
          <PhoneFrame device={device} scale={PREVIEW_SCALE}>
            <TemplateRenderer
              key={refreshKey}
              templateData={deferredTemplateData}
              viewportWidth={device.width}
              viewportHeight={device.height}
              scale={1}
              height={`${device.height}px`}
            />
          </PhoneFrame>
        )}
      </div>
    </div>
  )
}
